/**
 * EmployeesSection — fiches employés saisies par le client (Batch #14.251).
 * Identité · emploi · paiement · CCQ (si activée). Les crédits d'impôt, exemptions, vacances et
 * retenues récurrentes restent chez le gestionnaire (fiche marquée « à compléter »).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { HardHat, Loader2, Pencil, Plus, Users } from "lucide-react";
import { API, portalHeaders, SectionCard, addBtnCls, FREQ_LABELS, PAY_TYPES, PAYMENT_METHODS, err } from "./_shared";
import EmployeePortalForm from "./EmployeePortalForm";

export default function EmployeesSection({ summary, primaryColor, onChanged }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | employee
  const [showInactive, setShowInactive] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/paie/employees`, { headers: portalHeaders() });
      setItems(r.data.items || []);
    } catch (e) { err(e, "Erreur chargement des employés"); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const done = () => { setEditing(null); load(); onChanged?.(); };
  const visible = (items || []).filter((e) => showInactive || e.status === "active");

  return (
    <SectionCard title="Mes employés" icon={Users} primaryColor={primaryColor} testId="paie-portal-employees"
      action={!editing && (
        <button type="button" onClick={() => setEditing("new")} className={addBtnCls} style={{ backgroundColor: primaryColor }} data-testid="paie-portal-emp-add">
          <Plus className="w-4 h-4" /> Ajouter un employé
        </button>
      )}>
      {editing && (
        <EmployeePortalForm initial={editing === "new" ? null : editing} summary={summary} primaryColor={primaryColor} onDone={done} onCancel={() => setEditing(null)} />
      )}
      {items === null ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-300 mx-auto" /></div>
        : visible.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8" data-testid="paie-portal-emp-empty">
            Aucun employé pour l'instant. Ajoutez une première fiche : votre gestionnaire la complétera (crédits d'impôt, vacances).
          </p>
        ) : (
          <ul className="divide-y" data-testid="paie-portal-emp-list">
            {visible.map((e) => (
              <li key={e.id} className="py-3 flex items-start gap-3" data-testid={`paie-portal-emp-row-${e.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 flex items-center gap-1.5 flex-wrap">
                    {e.display_name}
                    {e.ccq?.active && <HardHat className="w-3.5 h-3.5 text-amber-600" title="CCQ" />}
                    {e.status !== "active" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 border">Inactif</span>}
                    {e.needs_review && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`paie-portal-emp-review-${e.id}`}>En vérification</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {e.employee_no}{e.job_title ? ` · ${e.job_title}` : ""} · {FREQ_LABELS[e.pay_frequency]} · {PAY_TYPES[e.pay_type]}
                    {e.pay_type === "hourly" ? ` ${Number(e.hourly_rate || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2 })} $/h` : ` ${Number(e.annual_salary || 0).toLocaleString("fr-CA")} $/an`}
                    {" · "}{PAYMENT_METHODS[e.payment_method]}{e.bank_account_masked ? ` ${e.bank_account_masked}` : ""}
                  </div>
                </div>
                <button type="button" onClick={() => setEditing(e)} className="p-2.5 -mr-2 text-gray-400 hover:text-gray-700 tap-target" title="Modifier" data-testid={`paie-portal-emp-edit-${e.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      {(items || []).some((e) => e.status !== "active") && (
        <label className="inline-flex items-center gap-1.5 text-xs text-gray-500 mt-2 cursor-pointer">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="paie-portal-emp-show-inactive" /> Afficher les employés inactifs
        </label>
      )}
    </SectionCard>
  );
}
